import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { isWmsSotEnabledForLocation } from "@redotech/redo-fulfillment-wms-common/wms-constants";
import type { LocationId } from "@redotech/redo-inventory-id";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { RedoOutboundLabelsRpcClientContext } from "../../outbound-labels-rpc-client";

/**
 * Resolves a group's origin (a Shopify location) to its Redo location, and
 * whether Redo owns on-hand there (WMS source of truth).
 */
export function useOriginWmsLocation(
  fulfillmentGroup:
    | Pick<FulfillmentGroupData, "selectedLocationId">
    | undefined,
): {
  originLocationId: LocationId | undefined;
  wmsSot: boolean;
  loading: boolean;
  error: boolean;
} {
  const rpcClient = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const team = useRequiredContext(TeamContext);
  const selectedLocationId = fulfillmentGroup?.selectedLocationId;

  const originLocationLoad = useLoad(
    async (signal) => {
      if (!selectedLocationId) {
        return null;
      }
      const { locations } = await rpcClient.getCrdbLocations(
        { includeManagedMerchantLocations: true },
        { signal },
      );
      return (
        locations.find((location) => location.externalId === selectedLocationId)
          ?.id ?? null
      );
    },
    [rpcClient, selectedLocationId],
  );

  const originLocationId = originLocationLoad.value ?? undefined;
  return {
    originLocationId,
    wmsSot:
      !!originLocationId &&
      isWmsSotEnabledForLocation(
        team.settings?.outboundLabels?.wms,
        originLocationId,
      ),
    // useLoad starts idle, so "no result yet" counts as loading too.
    loading:
      originLocationLoad.pending ||
      (originLocationLoad.value === undefined && !originLocationLoad.error),
    error: !!originLocationLoad.error,
  };
}
